import { atom, createStore } from 'jotai';
import { debounce, Sentry } from '@idk-web/core-utils';
import { LocalStorage } from '@idk-web/core-ui';
import {
  privateAuthState,
  setPrivateAuthState,
  type PrivateAuthState,
} from '@/state/privateAuth';
import { localeState, setLocaleState, type LocaleState } from '@/state/locale';

const STORAGE_KEY = 'idk-private-app-state-2026-04-22';
const PERSIST_INTERVAL_MS = 10000;

export const store = createStore();

type RootState = {
  locale: LocaleState;
  privateAuth: PrivateAuthState;
};

const state = atom<RootState>((get) => ({
  locale: get(localeState),
  privateAuth: get(privateAuthState),
}));

function readState() {
  const state = LocalStorage.getItem(STORAGE_KEY);

  try {
    return state ? JSON.parse(state) : undefined;
  } catch (e: unknown) {
    Sentry.reportError('Failed to parse state', { error: e });
    return undefined;
  }
}

function loadState(state: Partial<RootState>) {
  if (state.locale) {
    setLocaleState(state.locale);
  }

  if (state.privateAuth) {
    setPrivateAuthState(state.privateAuth);
  }
}

function persistState(state: RootState) {
  LocalStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

const persistedState = readState();

if (persistedState) {
  loadState(persistedState);
}

const persist = debounce(() => {
  persistState(store.get(state));
}, PERSIST_INTERVAL_MS);

store.sub(state, persist);
